import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RagBadge } from "@/components/common/RagBadge";
import { ClockCard } from "@/components/people/ClockCard";
import { CorrectionDialog } from "@/components/people/CorrectionDialog";
import { fmtDay } from "@/lib/format";
import { CORRECTION_STATUS, fmtDur, fmtTime } from "@/lib/people";
import { toast } from "sonner";

function EntryRow({ e, tz, onCorrect }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-[#EEF1F6] px-4 py-2.5 last:border-0" data-testid={`time-entry-${e.id}`}>
      <span className="w-28 text-sm text-[#0B1729]">{fmtTime(e.clock_in, tz)} – {e.clock_out ? fmtTime(e.clock_out, tz) : "now"}</span>
      <span className="text-sm font-semibold text-[#0B1729]">{fmtDur(e.minutes)}</span>
      {e.corrected && <RagBadge rag="blue" dot={false} className="!py-0 !text-[10px]">Corrected</RagBadge>}
      {e.correction_pending && <RagBadge rag="amber" dot={false} className="!py-0 !text-[10px]">Correction pending</RagBadge>}
      {!e.correction_pending && (
        <button onClick={() => onCorrect(e)} className="ml-auto text-xs font-medium text-[#4A78A8] hover:underline" data-testid={`correct-entry-${e.id}`}>Request correction</button>
      )}
    </div>
  );
}

export function MyTimeTab({ orgId, data, onChanged }) {
  const [correcting, setCorrecting] = useState(null);
  const tz = data.timezone;
  const byDay = data.entries.reduce((m, e) => ({ ...m, [e.local_date]: [...(m[e.local_date] || []), e] }), {});
  const withdraw = async (c) => {
    try { await api.post(`/workspace/clock/corrections/${c.id}/withdraw`); toast.success("Correction withdrawn"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="my-time-tab">
      <ClockCard orgId={orgId} data={data} onChanged={onChanged} />
      <div className="mb-3 mt-6 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold text-[#0B1729]">Your time records — last 14 days</h3>
        <Button size="sm" variant="outline" onClick={() => setCorrecting({})} data-testid="add-missing-record-btn"><Icons.Plus size={14} className="mr-1" /> Add a missing record</Button>
      </div>
      <p className="mb-3 text-xs text-[#1E2D44]/50">Worked time only — Compass doesn't calculate pay, overtime or breaks.</p>
      {Object.keys(byDay).length === 0 ? <p className="rounded-xl border border-dashed border-[#D2D9E3] px-4 py-6 text-center text-sm text-[#1E2D44]/45">No time recorded yet.</p> : (
        <div className="space-y-3" data-testid="time-days">
          {Object.entries(byDay).map(([day, list]) => (
            <div key={day} className="amden-card overflow-hidden p-0">
              <div className="flex justify-between bg-[#F7F9FC] px-4 py-2 text-xs font-semibold text-[#1E2D44]/65">
                <span>{fmtDay(day)}</span><span>{fmtDur(list.reduce((s, e) => s + e.minutes, 0))}</span>
              </div>
              {list.map((e) => <EntryRow key={e.id} e={e} tz={tz} onCorrect={setCorrecting} />)}
            </div>
          ))}
        </div>
      )}
      {data.corrections.length > 0 && (
        <section className="mt-7" data-testid="my-corrections">
          <h3 className="mb-2.5 font-display text-sm font-semibold text-[#0B1729]">Correction requests</h3>
          <div className="space-y-2">
            {data.corrections.map((c) => (
              <div key={c.id} className="amden-card flex flex-wrap items-center gap-3 p-4" data-testid={`my-correction-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0B1729]">{fmtDay(c.date)} · {fmtTime(c.proposed_clock_in, tz)} – {fmtTime(c.proposed_clock_out, tz)}</div>
                  <div className="text-xs text-[#1E2D44]/55">{c.reason}{c.decision_note ? ` · “${c.decision_note}”` : ""}{c.approver_name ? ` · ${c.approver_name}` : ""}</div>
                </div>
                <RagBadge rag={CORRECTION_STATUS[c.status].rag}>{CORRECTION_STATUS[c.status].label}</RagBadge>
                {c.status === "pending" && <Button size="sm" variant="outline" onClick={() => withdraw(c)} data-testid={`withdraw-correction-${c.id}`}>Withdraw</Button>}
              </div>
            ))}
          </div>
        </section>
      )}
      {correcting && (
        <CorrectionDialog orgId={orgId} entry={correcting.id ? correcting : null} tz={tz} today={data.today}
          onClose={() => setCorrecting(null)} onSaved={() => { setCorrecting(null); onChanged(); }} />
      )}
    </div>
  );
}
